export default [
  {
    "id": "premiers-pas",
    "category": "Prise en main",
    "title": "Se repérer et choisir son rôle",
    "roles": [],
    "path": "",
    "summary": "Retrouvez les outils utiles à votre métier sans parcourir toute l’application.",
    "steps": [
      "Ouvrez le menu de votre profil, en haut à droite. Le rôle sélectionné détermine les menus et les fonctions proposés.",
      "Si plusieurs rôles vous sont attribués, choisissez celui correspondant au travail à effectuer. Un rôle absent doit être attribué par un administrateur.",
      "Utilisez la barre latérale pour ouvrir une fonctionnalité. Les points verticaux d’une ligne donnent accès à ses actions.",
      "Ouvrez Guide d’utilisation en bas de la barre latérale. Recherchez un mot ou choisissez une rubrique ; le contenu suit votre rôle actif.",
      "Les nouveautés sont accessibles depuis l’icône étoilée du header. Elles dépendent de l’interface utilisée."
    ],
    "tip": "Changer de rôle ne crée pas de nouveaux droits. Si une rubrique manque, vérifiez aussi que vous êtes dans la bonne interface : Clinique, Caisse ou Stock."
  },
  {
    "id": "session",
    "category": "Prise en main",
    "title": "Résoudre une session expirée ou un chargement impossible",
    "roles": [],
    "path": "",
    "summary": "Comprendre les messages d’accès et reprendre une opération sans la créer deux fois.",
    "steps": [
      "Si le message indique une session expirée, reconnectez-vous dans l’interface concernée. La session Clinique et la session Caisse sont distinctes.",
      "Après connexion, rouvrez la fonctionnalité et utilisez Réessayer si ce bouton est proposé.",
      "Si l’accès est refusé, vérifiez votre rôle actif et contactez un administrateur. Ne modifiez pas les données pour contourner un refus d’accès.",
      "Après une erreur au moment d’une validation, consultez d’abord la liste ou l’historique pour savoir si l’opération a été enregistrée avant de recommencer.",
      "Si le problème persiste, transmettez au support le nom de l’écran, l’heure et le texte exact du message, sans mot de passe ni données médicales inutiles."
    ],
    "tip": "Une liste vide n’est pas toujours une panne : effacez les filtres de recherche, de date ou de statut. Ne multipliez pas les clics sur Enregistrer pendant un chargement."
  },
  {
    "id": "rechercher-imprimer",
    "category": "Prise en main",
    "title": "Rechercher, filtrer et imprimer un document",
    "roles": [],
    "path": "",
    "summary": "Retrouver rapidement une ligne et vérifier ce qui sera imprimé.",
    "steps": [
      "Saisissez un nom ou une référence dans le champ Rechercher de la liste concernée.",
      "Choisissez le mois, la date ou le statut disponibles. Vérifiez la période affichée avant d’interpréter les totaux.",
      "Ouvrez les détails de la ligne ou son menu de points verticaux, puis l’option d’impression lorsqu’elle est proposée.",
      "Contrôlez l’identité, la référence, les montants et la période dans l’aperçu avant d’imprimer."
    ],
    "tip": "Si une ligne attendue est absente, retirez les filtres et vérifiez la pagination. N’envoyez un document contenant des données personnelles qu’à un destinataire autorisé."
  },
  {
    "id": "tableau-clinique",
    "category": "Patients et soins",
    "title": "Lire son tableau de bord métier",
    "roles": [
      "ROLE_ADMINISTRATEUR",
      "ROLE_MEDECIN",
      "ROLE_INFIRMIER",
      "ROLE_SAGE_FEMME",
      "ROLE_TECH_SUP_LABO",
      "ROLE_MEDECIN_TRAVAIL",
      "ROLE_INFIRMIER_MEDECINE_TRAVAIL"
    ],
    "path": "/dashboard",
    "summary": "Retrouver les patients, demandes et activités correspondant à votre rôle.",
    "steps": [
      "Ouvrez Tableau de bord et vérifiez le rôle actif dans votre profil.",
      "Consultez les listes et indicateurs proposés pour ce métier : patients, consultations ou demandes en attente.",
      "Utilisez les filtres de période ou la recherche pour cibler votre travail.",
      "Ouvrez une ligne pour poursuivre son parcours, puis vérifiez l’identité du patient avant toute saisie."
    ],
    "tip": "Les indicateurs affichés varient selon le rôle. Une liste vide peut provenir de la période ou d’un statut sélectionné ; ne créez pas un deuxième dossier pour y remédier."
  },
  {
    "id": "dossier-patient",
    "category": "Patients et soins",
    "title": "Retrouver et consulter un dossier patient",
    "roles": [
      "ROLE_ADMINISTRATEUR",
      "ROLE_MEDECIN",
      "ROLE_INFIRMIER",
      "ROLE_SAGE_FEMME"
    ],
    "path": "/patients",
    "summary": "Accéder au dossier correct avant de documenter les soins.",
    "steps": [
      "Ouvrez Patients et recherchez la personne par les informations disponibles.",
      "Comparez les noms, l’âge et le numéro de dossier avant d’ouvrir la fiche.",
      "Consultez les rubriques du dossier et l’historique utiles à la prise en charge.",
      "Utilisez uniquement les actions de saisie proposées à votre rôle et vérifiez le résultat après enregistrement."
    ],
    "tip": "Ne vous fiez pas au nom seul en cas d’homonymie. Le guide porte sur l’utilisation de l’outil, pas sur les décisions médicales."
  },
  {
    "id": "consultation",
    "category": "Patients et soins",
    "title": "Renseigner une consultation",
    "roles": [
      "ROLE_MEDECIN"
    ],
    "path": "/dashboard/salle-attente",
    "summary": "Poursuivre la consultation depuis la salle d’attente jusqu’à son enregistrement.",
    "steps": [
      "Ouvrez la salle d’attente et choisissez la consultation du patient attendu.",
      "Vérifiez son identité, le bon et la spécialité avant de commencer.",
      "Consultez les informations existantes puis renseignez les champs du dossier adaptés à la consultation.",
      "Ajoutez les prescriptions ou actes nécessaires via les rubriques prévues.",
      "Relisez la consultation, enregistrez et contrôlez son état avant de quitter le dossier."
    ],
    "tip": "L’affichage de certains champs dépend de leur accessibilité par spécialité. Demandez une vérification du paramétrage si une constante attendue n’apparaît pas."
  },
  {
    "id": "prescriptions",
    "category": "Patients et soins",
    "title": "Ajouter une prescription au dossier",
    "roles": [
      "ROLE_MEDECIN"
    ],
    "path": "/patients",
    "summary": "Documenter les demandes d’analyses, d’examens, d’actes ou de traitements.",
    "steps": [
      "Ouvrez le dossier du patient dans le contexte de la consultation concernée.",
      "Choisissez la rubrique de prescription correspondant à la prestation demandée.",
      "Sélectionnez les éléments du catalogue et renseignez les indications ou modalités demandées.",
      "Relisez le contenu avant d’enregistrer, puis vérifiez les prescriptions présentes au dossier.",
      "Utilisez les documents ou impressions proposés pour transmettre la prescription dans le parcours prévu."
    ],
    "tip": "Les tarifs et les paiements se contrôlent en caisse. Ne dupliquez pas une prescription pour résoudre un problème de facturation."
  },
  {
    "id": "constantes-saisie",
    "category": "Patients et soins",
    "title": "Saisir les constantes du patient",
    "roles": [
      "ROLE_INFIRMIER",
      "ROLE_MEDECIN",
      "ROLE_SAGE_FEMME",
      "ROLE_ADMINISTRATEUR"
    ],
    "path": "/dashboard",
    "summary": "Choisir la bonne unité et conserver une mesure correctement datée.",
    "steps": [
      "Depuis votre tableau de bord ou le dossier patient, ouvrez la saisie des constantes autorisée à votre rôle.",
      "Vérifiez le patient et la date de la mesure.",
      "Saisissez les valeurs dans les champs disponibles et contrôlez l’unité de chaque constante.",
      "Relisez les valeurs et les éventuels badges d’interprétation avant d’enregistrer.",
      "Vérifiez que la saisie apparaît dans le dossier ou le relevé des constantes."
    ],
    "tip": "Les badges suivent les intervalles configurés ; ils ne remplacent pas une évaluation clinique. Une unité ou un champ absent doit être vérifié dans Dossier & Soins."
  },
  {
    "id": "admissions",
    "category": "Patients et soins",
    "title": "Suivre le parcours d’un patient",
    "roles": [
      "ROLE_MEDECIN",
      "ROLE_INFIRMIER"
    ],
    "path": "/parcours-patients",
    "summary": "Retrouver la localisation des patients et les informations de leur séjour.",
    "steps": [
      "Ouvrez Parcours patient et recherchez le patient concerné.",
      "Vérifiez l’identité et les dates avant d’ouvrir les détails.",
      "Consultez les informations et les rubriques de suivi disponibles pour votre rôle.",
      "Enregistrez les modifications autorisées, puis contrôlez l’état du séjour."
    ],
    "tip": "Vérifiez le séjour actif : un patient peut avoir plusieurs admissions dans son historique. Ne modifiez pas un ancien séjour pour documenter une prise en charge nouvelle."
  },
  {
    "id": "rma",
    "category": "Activité et rapports",
    "title": "Consulter le rapport mensuel d’activité",
    "roles": [
      "ROLE_ADMINISTRATEUR",
      "ROLE_MEDECIN",
      "ROLE_INFIRMIER",
      "ROLE_SAGE_FEMME"
    ],
    "path": "/rma",
    "summary": "Lire l’activité sur le mois choisi et préparer le contrôle des données.",
    "steps": [
      "Ouvrez RMA depuis la barre latérale.",
      "Sélectionnez la période et les filtres proposés.",
      "Consultez les regroupements et indicateurs affichés, puis vérifiez les éléments qui demandent une correction.",
      "Utilisez les actions d’export ou d’impression disponibles après avoir confirmé la période."
    ],
    "tip": "Un rapport dépend des données enregistrées dans les dossiers. Corrigez une information à sa source selon vos droits, et non en inventant un total attendu."
  },
  {
    "id": "labo-resultats",
    "category": "Laboratoire",
    "title": "Retrouver une analyse et saisir ses résultats",
    "roles": [
      "ROLE_TECH_SUP_LABO"
    ],
    "path": "/registre/analyses",
    "summary": "Poursuivre le circuit de résultat à partir du registre.",
    "steps": [
      "Ouvrez Registre analyse et recherchez la demande du patient.",
      "Vérifiez l’identité, l’analyse et l’état de la demande.",
      "Ouvrez la saisie des résultats et complétez les champs du modèle, avec les unités demandées.",
      "Relisez les valeurs puis utilisez l’enregistrement ou la validation disponible dans votre parcours.",
      "Contrôlez le résultat dans le registre et utilisez l’impression lorsqu’elle est proposée."
    ],
    "tip": "Ne saisissez pas un résultat dans la demande d’un homonyme. Si le modèle ou une unité manque, faites vérifier le catalogue avant la validation."
  },
  {
    "id": "labo-modeles",
    "category": "Laboratoire",
    "title": "Paramétrer les modèles et analyses",
    "roles": [
      "ROLE_ADMINISTRATEUR",
      "ROLE_TECH_SUP_LABO"
    ],
    "path": "/parametres/laboratoire-imagerie",
    "summary": "Préparer un catalogue et des formulaires de résultats cohérents.",
    "steps": [
      "Ouvrez Paramètres → Laboratoire & Imagerie.",
      "Retrouvez l’analyse ou le modèle à configurer ; recherchez d’abord l’existant pour éviter les doublons.",
      "Complétez les champs, unités et informations du modèle proposés par l’écran.",
      "Relisez les réglages puis enregistrez.",
      "Vérifiez le modèle dans le parcours de saisie avant son utilisation habituelle."
    ],
    "tip": "La modification d’un modèle doit être validée par le responsable du laboratoire. Ce guide n’établit pas de valeurs biologiques de référence."
  },
  {
    "id": "imagerie-cliches",
    "category": "Imagerie",
    "title": "Ajouter les clichés et résultats d’un examen",
    "roles": [
      "ROLE_TECHNICIEN_IMAGERIE",
      "ROLE_RADIOLOGUE"
    ],
    "path": "/examens/porter-resultat",
    "summary": "Retrouver une demande et lui associer les bons fichiers.",
    "steps": [
      "Ouvrez Examens → Clichés et recherchez la demande.",
      "Vérifiez le patient et la référence, puis cliquez directement sur le badge de l’examen pour ouvrir ses clichés et son compte rendu. Le petit + affiche les informations complémentaires du patient.",
      "Dans la salle d’examen, sélectionnez plusieurs clichés JPEG, PNG, WebP ou PDF : 5 Mo maximum par fichier, 20 clichés par examen, archive comprise.",
      "Contrôlez leur correspondance au patient, puis envoyez le lot. Ouvrez chaque aperçu pour vérifier sa lisibilité ; d’autres clichés peuvent être ajoutés ensuite.",
      "Pour retirer un fichier ajouté par ce circuit, indiquez le motif. Un compte rendu validé verrouille les clichés ; l’ancien fichier d’archive est conservé en lecture seule."
    ],
    "tip": "N’ajoutez jamais un cliché sur la seule base du nom du fichier. Vérifiez l’identité et la demande ; l’interprétation suit le rôle et le parcours prévus."
  },
  {
    "id": "imagerie-interpreter",
    "category": "Imagerie",
    "title": "Renseigner une interprétation",
    "roles": [
      "ROLE_RADIOLOGUE"
    ],
    "path": "/examens/interpreter",
    "summary": "Traiter les examens en attente d’interprétation.",
    "steps": [
      "Ouvrez Examens → Interprétations.",
      "Sélectionnez la demande et vérifiez le patient, l’examen et les documents joints.",
      "Rédigez librement un titre et un compte rendu. Un modèle est facultatif : sélectionnez-le puis cliquez sur Insérer le modèle. Confirmez le remplacement seulement si vous souhaitez remplacer le texte déjà saisi.",
      "Enregistrez le brouillon. Il reste modifiable et n’est pas encore publié dans le dossier médecin. Même si les modèles sont indisponibles, la rédaction libre reste possible.",
      "Après relecture du brouillon enregistré, cochez la confirmation et cliquez sur Valider le compte rendu. Cette action est réservée au radiologue autorisé pour cet examen.",
      "Retrouvez le résultat dans le registre. Pour corriger un rapport validé, choisissez Préparer une correction, précisez son motif, enregistrez puis validez à nouveau. La dernière version validée reste publiée pendant la correction."
    ],
    "tip": "Ce parcours est destiné au rôle Radiologue. Un problème d’accès doit être traité par l’administrateur, pas par l’utilisation du compte d’un collègue."
  },
  {
    "id": "imagerie-registre",
    "category": "Imagerie",
    "title": "Consulter le registre des examens",
    "roles": [
      "ROLE_TECHNICIEN_IMAGERIE",
      "ROLE_RADIOLOGUE"
    ],
    "path": "/examens/registre",
    "summary": "Retrouver les examens et suivre leur avancement.",
    "steps": [
      "Ouvrez Examens → Registre.",
      "Recherchez le patient, le prescripteur ou un renseignement clinique ; choisissez éventuellement le type d’examen.",
      "Filtrez par état : À réaliser, À interpréter, Brouillon, Validé ou Archive. Les compteurs et le nombre de clichés permettent de suivre l’avancement.",
      "Le petit + devant le patient affiche ses informations complémentaires. Cliquez sur Ouvrir sur la ligne de l’examen souhaité.",
      "Ouvrez les clichés et le compte rendu. L’historique distingue les brouillons des versions validées ; les anciens documents restent disponibles.",
      "Utilisez Imprimer la version validée puis choisissez une imprimante ou Enregistrer au format PDF dans le dialogue du navigateur. Le document contient l’identité, l’examen, le contexte, l’auteur, la date et la version publiée. Un brouillon ou une correction non validée ne remplace pas cette version."
    ],
    "tip": "Vérifiez la différence entre un examen demandé, un cliché ajouté et une interprétation validée. Ces étapes n’ont pas le même état d’avancement."
  },
  {
    "id": "profil",
    "category": "Paramétrages",
    "title": "Consulter son profil professionnel",
    "roles": [
      "ROLE_ADMINISTRATEUR",
      "ROLE_MEDECIN",
      "ROLE_TECH_SUP_LABO",
      "ROLE_RADIOLOGUE",
      "ROLE_TECHNICIEN_IMAGERIE"
    ],
    "path": "/parametres/profil",
    "summary": "Vérifier les informations associées à votre compte.",
    "steps": [
      "Ouvrez Paramètres → Mon profil, ou Mon profil depuis le menu de votre compte.",
      "Vérifiez votre identité et vos informations professionnelles.",
      "Modifiez uniquement les champs autorisés par l’écran, puis enregistrez.",
      "Pour un rôle manquant ou un accès incorrect, contactez l’administrateur."
    ],
    "tip": "Ne partagez pas vos identifiants. Chaque intervenant doit utiliser son propre compte pour conserver une traçabilité fiable."
  },
  {
    "id": "etablissement",
    "category": "Paramétrages",
    "title": "Configurer l’établissement et la facturation",
    "roles": [
      "ROLE_ADMINISTRATEUR"
    ],
    "path": "/parametres/etablissement",
    "summary": "Retrouver les informations générales et les options des documents.",
    "steps": [
      "Ouvrez Paramètres → Établissement et développez la rubrique concernée.",
      "Vérifiez les informations de l’établissement et le fuseau horaire défini dans les réglages.",
      "Dans Document et facturation, choisissez les options de commentaire, de référence et de date affichées sur les factures.",
      "Enregistrez la rubrique modifiée, puis vérifiez le document ou l’écran concerné."
    ],
    "tip": "Un réglage d’établissement peut affecter plusieurs utilisateurs. Vérifiez les valeurs avant enregistrement ; le fuseau du poste ne remplace pas celui défini dans les paramètres."
  },
  {
    "id": "activation-prescripteurs",
    "category": "Paramétrages",
    "title": "Activer ou désactiver les commissions des prescripteurs",
    "roles": [
      "ROLE_ADMINISTRATEUR"
    ],
    "path": "/parametres/etablissement",
    "summary": "Décider si la caisse propose un prescripteur et génère les commissions.",
    "steps": [
      "Ouvrez Établissement → Document et facturation.",
      "Dans Commissions des prescripteurs, choisissez Oui pour activer ou Non pour désactiver.",
      "Cliquez sur Enregistrer les commissions et attendez le message de confirmation.",
      "Si le chargement du paramètre échoue, reconnectez-vous à la Clinique lorsqu’une expiration est indiquée, puis réessayez.",
      "Rouvrez la fenêtre de paiement dans la Caisse pour vérifier le comportement correspondant au réglage."
    ],
    "tip": "La désactivation conserve les fiches et les commissions déjà acquises. La réactivation ne recalcule pas les anciennes opérations. La commission se déclenche à la première validation, même partielle, différée ou à zéro."
  },
  {
    "id": "personnel",
    "category": "Paramétrages",
    "title": "Gérer le personnel et ses accès",
    "roles": [
      "ROLE_ADMINISTRATEUR"
    ],
    "path": "/parametres/equipe-acces",
    "summary": "Organiser les comptes et les rôles du personnel.",
    "steps": [
      "Ouvrez Équipe et Accès → Personnel et prescripteurs.",
      "Dans Personnel & Externes, recherchez la personne avant de créer une fiche.",
      "Utilisez Nouveau personnel ou les points verticaux d’une fiche pour ouvrir les actions disponibles.",
      "Renseignez l’identité professionnelle et attribuez uniquement les rôles nécessaires.",
      "Enregistrez et vérifiez les rôles affichés sur la ligne."
    ],
    "tip": "Les prescripteurs externes se gèrent dans l’autre onglet. Une fiche de prescripteur ne crée pas automatiquement un compte de connexion."
  },
  {
    "id": "regles-prescripteurs",
    "category": "Paramétrages",
    "title": "Configurer un prescripteur et ses pourcentages",
    "roles": [
      "ROLE_ADMINISTRATEUR"
    ],
    "path": "/parametres/equipe-acces",
    "summary": "Définir une commission par catégorie de prestation.",
    "steps": [
      "Ouvrez Personnel et prescripteurs, puis l’onglet Prescripteurs externes, repéré par son trait bleu lorsqu’il est sélectionné.",
      "Recherchez une fiche existante ou cliquez sur Ajouter un prescripteur.",
      "Renseignez le nom, les prénoms et le contact.",
      "Ajoutez les catégories concernées : Analyses, Examens, Consultations, Chirurgies ou les autres catégories proposées.",
      "Saisissez le pourcentage de chaque catégorie puis enregistrez. Le taux s’applique au PU multiplié par la quantité de la prestation."
    ],
    "tip": "Il n’y a pas de base de commission à remplacer ni de choix d’élément individuel. Une catégorie sans règle ou à 0 % ne produit pas de commission. Les règles nouvelles ne recalculent pas les commissions déjà acquises."
  },
  {
    "id": "constantes-param",
    "category": "Paramétrages",
    "title": "Paramétrer les constantes en quatre étapes",
    "roles": [
      "ROLE_ADMINISTRATEUR",
      "ROLE_MEDECIN",
      "ROLE_RADIOLOGUE",
      "ROLE_TECHNICIEN_IMAGERIE"
    ],
    "path": "/parametres/dossier-soins",
    "summary": "Configurer le champ, ses unités, ses intervalles et son accessibilité.",
    "steps": [
      "Ouvrez Dossier & Soins et la rubrique Constantes. Choisissez une constante existante ou l’ajout d’une constante.",
      "Dans Champ et unités, vérifiez le nom, le type, le statut, l’activation du champ et les unités. Choisissez l’unité par défaut.",
      "Dans Intervalles et badges, renseignez les règles proposées et contrôlez leur cohérence dans l’unité de référence.",
      "Dans Vérification, relisez le paramétrage avant de poursuivre.",
      "Dans Accessibilité, cochez les spécialités de consultation dans lesquelles le champ doit apparaître, puis enregistrez."
    ],
    "tip": "Les unités, intervalles et interprétations doivent être validés par le responsable médical. Si un champ n’apparaît pas, vérifiez son activation et la spécialité sélectionnée dans Accessibilité."
  },
  {
    "id": "dossier-param",
    "category": "Paramétrages",
    "title": "Organiser les champs et protocoles du dossier",
    "roles": [
      "ROLE_ADMINISTRATEUR",
      "ROLE_MEDECIN",
      "ROLE_RADIOLOGUE",
      "ROLE_TECHNICIEN_IMAGERIE"
    ],
    "path": "/parametres/dossier-soins",
    "summary": "Retrouver les réglages de dossier et de soins disponibles à votre rôle.",
    "steps": [
      "Ouvrez Dossier & Soins et développez la rubrique voulue.",
      "Recherchez le champ ou le protocole existant avant d’en créer un.",
      "Complétez les informations et l’accessibilité proposées par le formulaire.",
      "Relisez puis enregistrez, et vérifiez l’affichage dans la spécialité concernée."
    ],
    "tip": "Les constantes utilisent leur assistant dédié. Les autorisations et le fonctionnement des autres protocoles restent ceux de leur parcours existant."
  },
  {
    "id": "catalogue-tarifs",
    "category": "Paramétrages",
    "title": "Gérer les prestations et les tarifs",
    "roles": [
      "ROLE_ADMINISTRATEUR"
    ],
    "path": "/parametres/prestations-tarifs",
    "summary": "Préparer les prix utilisés dans les bons et les ventes.",
    "steps": [
      "Ouvrez Prestations & Tarifs et choisissez la catégorie concernée.",
      "Recherchez la prestation, puis ouvrez sa configuration ou l’ajout.",
      "Vérifiez le libellé, le prix et les options tarifaires proposées.",
      "Enregistrez et contrôlez le tarif dans le parcours de facturation approprié."
    ],
    "tip": "Ne confondez pas le prix facturé au patient et un pourcentage de rémunération. Les commissions des prescripteurs se règlent séparément dans Équipe et Accès."
  },
  {
    "id": "assurances-param",
    "category": "Paramétrages",
    "title": "Configurer assurances et conventions",
    "roles": [
      "ROLE_ADMINISTRATEUR"
    ],
    "path": "/parametres/assurances",
    "summary": "Organiser les sociétés, couvertures et conditions de prise en charge.",
    "steps": [
      "Ouvrez Assurances & Conventions et recherchez l’organisme concerné.",
      "Ouvrez la société ou la convention à configurer.",
      "Vérifiez les taux, bases, validités et, si applicable, le forfait ou les exclusions proposés dans le formulaire.",
      "Enregistrez les réglages puis contrôlez un calcul tarifaire dans le parcours caisse sans valider une opération fictive.",
      "En cas d’écart, comparez la société du patient et la convention réellement sélectionnée."
    ],
    "tip": "Une couverture à 100 % ou un forfait n’autorise pas toutes les prestations sans condition. Vérifiez les exclusions et autorisations prévues par la convention."
  },
  {
    "id": "mdt-societes",
    "category": "Médecine du travail",
    "title": "Gérer les sociétés et les travailleurs",
    "roles": [
      "ROLE_MEDECIN_TRAVAIL"
    ],
    "path": "/societes",
    "summary": "Retrouver une société et ses dossiers de travailleurs.",
    "steps": [
      "Ouvrez Sociétés et recherchez l’entreprise.",
      "Ouvrez sa fiche pour consulter ses informations et les travailleurs rattachés.",
      "Utilisez les actions d’ajout ou de modification disponibles pour compléter les informations requises.",
      "Vérifiez le rattachement à la bonne société avant d’enregistrer ou de poursuivre une visite."
    ],
    "tip": "Recherchez un travailleur avant de créer une nouvelle fiche. Un changement de société doit être traité dans le parcours prévu, sans réutiliser le dossier d’une autre personne."
  },
  {
    "id": "mdt-postes",
    "category": "Médecine du travail",
    "title": "Documenter une visite de poste",
    "roles": [
      "ROLE_MEDECIN_TRAVAIL"
    ],
    "path": "/visites-de-postes",
    "summary": "Écran actuellement en maintenance dans cette version.",
    "steps": [
      "Le menu Visites de postes ouvre actuellement une page En maintenance.",
      "Aucune saisie de visite n'est disponible sur cette page dans cette version.",
      "Utilisez la procédure opérationnelle de votre établissement en attendant sa disponibilité."
    ],
    "tip": "Distinguez la visite de poste d’une consultation individuelle. Renseignez uniquement les informations nécessaires et autorisées dans ce contexte."
  },
  {
    "id": "mdt-risques",
    "category": "Médecine du travail",
    "title": "Suivre les risques professionnels",
    "roles": [
      "ROLE_MEDECIN_TRAVAIL"
    ],
    "path": "/accident-maladie-pro",
    "summary": "Écran actuellement en maintenance dans cette version.",
    "steps": [
      "Le menu Risques professionnels affiche actuellement En maintenance.",
      "Ce menu n'est pas encore un registre opérationnel ; aucune déclaration n'y est enregistrée.",
      "Suivez le circuit en vigueur dans votre établissement."
    ],
    "tip": "Ce guide décrit la saisie dans l’application, pas les obligations réglementaires de déclaration. Faites valider ces démarches par le responsable compétent."
  },
  {
    "id": "mdt-rapports",
    "category": "Médecine du travail",
    "title": "Retrouver les rapports et certificats",
    "roles": [
      "ROLE_MEDECIN_TRAVAIL"
    ],
    "path": "/rapports",
    "summary": "La rubrique Rapports est actuellement en maintenance.",
    "steps": [
      "Le menu Rapports affiche actuellement En maintenance.",
      "La génération de rapports n'est pas disponible ici dans cette version.",
      "Ne considérez pas la présence du menu comme la création d'un document."
    ],
    "tip": "Un certificat nécessite la validation de la personne habilitée. Ne transmettez pas les données médicales à un destinataire non autorisé."
  },
  {
    "id": "mdt-document",
    "category": "Médecine du travail",
    "title": "Consulter le document unique",
    "roles": [
      "ROLE_MEDECIN_TRAVAIL"
    ],
    "path": "/document-unique",
    "summary": "Écran actuellement en maintenance dans cette version.",
    "steps": [
      "Le menu Document unique affiche actuellement En maintenance.",
      "Il ne permet pas encore de renseigner ni de générer un document.",
      "Conservez le circuit documentaire de votre établissement."
    ],
    "tip": "La présence d’un document dans l’outil ne suffit pas à attester sa conformité. Faites contrôler le contenu et sa diffusion par le responsable de l’établissement."
  },
  {
    "id": "mdt-facturation",
    "category": "Médecine du travail",
    "title": "Consulter la facturation des sociétés",
    "roles": [
      "ROLE_MEDECIN_TRAVAIL"
    ],
    "path": "/facturations",
    "summary": "La facturation dédiée à la médecine du travail est en maintenance.",
    "steps": [
      "Le menu Facturations affiche actuellement En maintenance.",
      "Aucune facture n'est créée ou réglée depuis cette page.",
      "Utilisez uniquement les circuits de caisse opérationnels et autorisés de votre établissement."
    ],
    "tip": "Vérifiez la société et les conditions de la convention. La consultation d’une facture ne signifie pas qu’un règlement a été encaissé."
  }
]
